import { ElementCommonPropertyConverter } from '@src/slide/domain/ui-model/converter/node-property/element/element-common-property-converter';
import { BorderConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/border/border-converter';
import { EffectConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/effect/effect-converter';
import { PictureInfoConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/picture-info/picture-info-converter';
import { FillConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/fill/fill-converter';
import { GraphicInfoConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/graphic-info/graphic-info-converter';
import { ILinkAttr, LinkConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/link/link-converter';
import { LockingConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/locking/locking-converter';
import { AttachmentConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/attachment/attachment-converter';
import { AddonConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/addon/addon-converter';
import { NameConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/name/name-converter';
import { PlaceholderConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/placeholder/placeholder-converter';
import { Element3DConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/3d/element-3d-converter';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { ShapeProps, DataModelChange, DataType, ICTTextParagraphProperties, IShapeData, IShapeProps, ICTBlipFillProperties, DeltaOf } from '@tencent/slide-sdk-model';
import { DeepPartial } from 'ts-essentials';
import { TextConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/text/text-converter';
import { CustomGraphicInfoConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/custom-graphic-info/custom-graphic-info-converter';
import { ShapeType } from '@src/slide/infrastructure/module/dependent/shape-info/graphic-type';
import { Locks } from '@src/slide/domain/ui-model/element-modeles/property/lock/lock.interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { IPictureImage } from '@src/slide/domain/ui-model/element-modeles/Interface/model-props/picture-model-props.interface';
import _cloneDeep from 'lodash/cloneDeep';
import ChangesetEngineV3 from '@src/slide/domain/ui-model/changesetManager/changeset-engine-v3';
import { TextParagraphPropsConverter } from '../module-property/text/text-paragraph/text-paragraph-props-converter';
import { TextParagraphCharacterConverter } from '../module-property/text/text-paragraph/text-paragraph-character-converter';
import { IParagraphCharacter, IParagraphStyle } from '../../../element-modeles/property/text/interface/editor-styles.interface';

export class ShapePropertyConverter {
    /**
     * 数据3.0转换接口
     * @param sdkData
     * @param uiData
     * @param changesetInfo
     */
    // eslint-disable-next-line complexity
    public static convertPartialDataFromSdkToUi(
        sdkData: DeepPartial<IShapeProps>,
        uiData: IShapeModel,
        changesetInfo?: DataModelChange | null,
    ): void {
        // 占位符信息转换
        if (sdkData.nvPr?.ph) {
            PlaceholderConverter.convertPartialDataFromSdkToUi(sdkData.nvPr.ph, uiData);
        }
        // 是否为文本框
        if (sdkData.nvPr?.txBox === true || sdkData.nvPr?.txBox === false) {
            uiData.props = uiData.props || {};
            uiData.props.style = uiData.props.style || {};
            uiData.props.subType = sdkData.nvPr?.txBox ? 1 : 0;
            uiData.props.style.subType = sdkData.nvPr?.txBox ? 1 : 0;
        }
        // 通用数据转换
        ElementCommonPropertyConverter.convertPartialDataFromSdkToUi(sdkData, uiData, changesetInfo);
        // 文本 frame 信息
        if (sdkData.txXfrm) {
            const uiFrame = ElementCommonPropertyConverter.getFrameInfoFromSdkToUi(sdkData.txXfrm, uiData);
            uiData.props = uiData.props || {};
            uiData.props.textFrame = uiFrame;
        }
        if (sdkData?.spPr) {
            // 图形信息转换
            if (sdkData.spPr.prstGeom) {
                GraphicInfoConverter.convertPartialDataFromSdkToUi(sdkData.spPr.prstGeom, uiData.props.style);
            }
            // 自定义图形信息转换
            if (sdkData.spPr.custGeom) {
                CustomGraphicInfoConverter.convertPartialDataFromSdkToUi(sdkData.spPr.custGeom, uiData.props.style);
            }
            // 填充
            if (FillConverter.needConvertSdkData(sdkData.spPr)) {
                // 对齐以前逻辑，每次都重新覆盖一个新对象
                uiData.props.style.fillBackground = {};
                FillConverter.convertPartialDataFromSdkToUi(sdkData.spPr, uiData.props.style.fillBackground);
                // 形状设置图片填充， 效果、矫正、重新着色没在fillBackground下，放在了image下
                if (sdkData.spPr.blipFill) {
                    uiData.props.style.image = uiData.props.style.image || {};
                    PictureInfoConverter
                        .convertImageImgPropsDataSdkToUi(sdkData.spPr.blipFill, uiData.props.style.image);
                    PictureInfoConverter
                        .convertRecolorDataSdkToUi(sdkData.spPr.blipFill, uiData.props.style.image);
                } else if (uiData.props.style.image) {
                    delete uiData.props.style.image;
                }
            } else if (uiData.props.style.fillBackground) {
                delete uiData.props.style.fillBackground;
                delete uiData.props.style.image;
            }
            // 边框
            if (sdkData.spPr?.ln) {
                uiData.props.style.border = uiData.props.style.border || {};
                BorderConverter.convertPartialDataFromSdkToUi(sdkData.spPr.ln, uiData.props.style.border);
                BorderConverter.convertControlValuesFromLineSketch(sdkData.spPr.ln, uiData.props.style);
            } else if (uiData.props.style.border) {
                delete uiData.props.style.border;
            }
            // 效果
            if (sdkData.spPr?.effectLst) {
                uiData.props.style.effect = uiData.props.style.effect || {};
                EffectConverter.convertPartialDataFromSdkToUi(sdkData.spPr.effectLst, uiData.props.style.effect);
            } else if (uiData.props.style.effect) {
                delete uiData.props.style.effect;
            }
            if (sdkData.spPr?.scene3d) {
                uiData.scene3d = uiData.scene3d ?? {};
                Element3DConverter.convertScene3DFromSdkToUi(sdkData.spPr.scene3d, uiData.scene3d);
            } else {
                uiData.scene3d = undefined;
            }
            if (sdkData.spPr?.sp3d) {
                uiData.shape3d = uiData.shape3d ?? {};
                Element3DConverter.convertShape3DFromSdkToUi(sdkData.spPr.sp3d, uiData.shape3d);
            } else {
                uiData.shape3d = undefined;
            }
        }
        // 锁定纵横比
        if (sdkData.nvPr) {
            LockingConverter.convertPartialDataFromSdkToUi(sdkData.nvPr.locking, uiData.props);
        }
        // 链接
        if (sdkData.nvPr?.hlinkClick) {
            LinkConverter.convertPartialDataFromSdkToUi(sdkData.nvPr.hlinkClick, uiData.props);
        } else if (uiData.props[ILinkAttr.link]) {
            delete uiData.props[ILinkAttr.link];
        }
        // 附件
        if (sdkData.nvPr?.attachment) {
            AttachmentConverter.convertPartialDataFromSdkToUi(sdkData.nvPr.attachment, uiData.props);
        }
        // 插件
        if (sdkData.nvPr?.addon) {
            AddonConverter.convertPartialDataFromSdkToUi(sdkData.nvPr.addon, uiData.props);
        }
        // 名称
        if (sdkData.nvPr?.name) {
            NameConverter.convertPartialDataFromSdkToUi(sdkData.nvPr, uiData);
        }
        // 文本 （存在changesetInfo时说明是sdk变更来的，检查是否命中来提高性能）
        if (
            sdkData.txBody
            && (!changesetInfo || ChangesetEngineV3.hasChangeAtPath(changesetInfo, 'txBody'))
        ) {
            TextConverter.convertPartialDataFromSdkToUi(sdkData.txBody, uiData);
        }
    }

    // eslint-disable-next-line complexity
    public static convertPartialDataFromUiToSdk(
        partialUiData: DeepPartial<IShapeModel>,
        toSdkData: DeltaOf<IShapeData>,
        rawUiData: IShapeModel,
        clearInherit = true,
    ): void {
        toSdkData.spPr = toSdkData.spPr || {};
        toSdkData.nvPr = toSdkData.nvPr || {};

        // 占位符信息转换
        const placeholderInfo = partialUiData.props?.style?.placeholder || partialUiData.placeHolder;
        if (placeholderInfo) {
            toSdkData.nvPr.ph = toSdkData.nvPr.ph || {};
            PlaceholderConverter.convertPartialDataFromUiToSdk(placeholderInfo, toSdkData.nvPr.ph);
        }

        // 是否为文本框
        if (partialUiData.props?.subType === 1 || partialUiData.props?.subType === 0) {
            toSdkData.nvPr.txBox = !!partialUiData.props.subType;
        }

        if (partialUiData.props?.style) {
            const style = partialUiData.props?.style;
            ElementCommonPropertyConverter.convertPartialDataFromUiToSdk(partialUiData, toSdkData, rawUiData);
            // 图形信息转换
            if (style?.bgShape || style?.controlValues) {
                if ((partialUiData.props.style?.bgShape || rawUiData.props.style?.bgShape) !== ShapeType.custom) {
                    toSdkData.spPr.prstGeom = GraphicInfoConverter.convertPartialDataFromUiToSdk({
                        // FIXME: 透传 rawUiData 是为了在后续转 sdkData 的时候判断类型
                        bgShape: style.bgShape ?? rawUiData.props.style?.bgShape,
                        controlValues: style.controlValues,
                    });
                    toSdkData.spPr.custGeom = null;
                } else {
                    // 草绘 历史遗留 后续考虑优化
                    // @ts-ignore
                    const { customGeom } = style;
                    if (customGeom?.pathList?.length) {
                        toSdkData.spPr.custGeom = toSdkData.spPr.custGeom || {};
                        CustomGraphicInfoConverter.convertPartialDataFromUiToSdk(customGeom, toSdkData.spPr.custGeom);
                    }
                    toSdkData.spPr.prstGeom = null;
                }
            }
            // 填充
            if (style?.fillBackground) {
                FillConverter.convertPartialDataFromUiToSdk(style?.fillBackground, toSdkData.spPr);
            } else if (style?.fillBackground === null) {
                ['grpFill', 'blipFill', 'pattFill', 'solidFill', 'gradFill', 'noFill'].forEach((p) => {
                    if (rawUiData?.sdkData?.props?.spPr?.[p] && toSdkData.spPr) {
                        toSdkData.spPr[p] = null;
                    }
                });
            }
            // 图片填充的 效果、矫正、重新着色
            if (style?.image) {
                toSdkData.spPr.blipFill = toSdkData.spPr.blipFill || {};
                PictureInfoConverter.convertImageImgPropsDataUiToSdk(
                    style.image as DeltaOf<IPictureImage>,
                    toSdkData.spPr.blipFill,
                    rawUiData.sdkData?.props?.spPr?.blipFill as ICTBlipFillProperties,
                );
                PictureInfoConverter.convertRecolorDataUiToSdk(
                    style.image as DeltaOf<IPictureImage>,
                    toSdkData.spPr.blipFill,
                );
            }
            // 边框
            if (style?.border) {
                toSdkData.spPr.ln = toSdkData.spPr.ln || {};
                BorderConverter.convertPartialDataFromUiToSdk(style.border, toSdkData.spPr.ln, rawUiData);
            } else if (style.border === null) {
                toSdkData.spPr.ln = null;
            }
            // 效果
            if (style?.effect) {
                toSdkData.spPr.effectLst = toSdkData.spPr.effectLst || {};
                EffectConverter.convertPartialDataFromUiToSdk(style?.effect, toSdkData.spPr.effectLst);
            }
        }
        // 3d
        if (partialUiData.props?.scene3d) {
            toSdkData.spPr.scene3d = toSdkData.spPr.scene3d ?? {};
            Element3DConverter.convertScene3DFromUiToSdk(partialUiData.props.scene3d, toSdkData.spPr.scene3d);
        } else if (partialUiData.props?.scene3d === null) {
            toSdkData.spPr.scene3d = null;
        }
        if (partialUiData.props?.shape3d) {
            toSdkData.spPr.sp3d = toSdkData.spPr.sp3d ?? {};
            Element3DConverter.convertShape3DFromUiToSdk(partialUiData.props.shape3d, toSdkData.spPr.sp3d);
        } else if (partialUiData.props?.shape3d === null) {
            toSdkData.spPr.sp3d = null;
        }
        // 锁定纵横比
        if (partialUiData.props?.[Locks.LOCKS]) {
            toSdkData.nvPr.locking = toSdkData.nvPr.locking || {};
            LockingConverter.convertPartialDataFromUiToSdk(partialUiData.props, toSdkData.nvPr.locking);
        }
        // 链接
        if (partialUiData.props?.link) {
            toSdkData.nvPr.hlinkClick = toSdkData.nvPr.hlinkClick || {};
            LinkConverter.convertPartialDataFromUiToSdk(partialUiData.props, toSdkData.nvPr.hlinkClick);
        } else if (partialUiData.props?.link === null) {
            toSdkData.nvPr.hlinkClick = null;
        }
        // 附件
        if (partialUiData.props?.attachment) {
            AttachmentConverter.convertPartialDataFromUiToSdk(partialUiData.props, toSdkData.nvPr);
        }
        // 名称
        if (partialUiData.name) {
            NameConverter.convertPartialDataFromUiToSdk(partialUiData, toSdkData.nvPr);
        }
        // 插件
        if (partialUiData.props?.addon) {
            AddonConverter.convertPartialDataFromUiToSdk(partialUiData.props, toSdkData.nvPr);
        }
        // 文本
        if (partialUiData.paragraphList || partialUiData.textBodyStyle) {
            toSdkData.txBody = toSdkData.txBody || {};
            TextConverter.convertPartialDataFromUiToSdk(partialUiData, toSdkData.txBody, clearInherit);
        }
    }

    /**
     * 2.0继承样式的获取是通过在上层通过自行merge stylelist来获取的
     * 3.0上层不再关注styleList，文字的继承属性是sdk直接组装好的
     * 但是新建、复制粘贴等特殊场景，在数据经过sdk插入前需要在页面渲染出来临时元素
     * 行高这些属性就需要继承后的，所以可以通过sdk的props来曲线获取到继承后的全量样式
     * @param uiData
     * @param slideData
     * @param needRawTag { boolean } 是否需要对自身数据打标
     */
    public static appendUiDataInheritProp(uiData: IShapeModel, slideData: ISlideModel, needRawTag = true): void {
        const beforeInheritSdkData = {};
        // 将uiData转成sdkData
        ShapePropertyConverter.convertPartialDataFromUiToSdk(uiData, beforeInheritSdkData, uiData);
        // 通过生成 sdkProps，让sdkData拥有对应slide的默认继承样式
        const afterInheritSdkData = ShapePropertyConverter
            .appendSdkDataInheritProp(beforeInheritSdkData as IShapeData, slideData);
        // 将sdkData转换回uiData
        const rawParagraphList = needRawTag && _cloneDeep(uiData.paragraphList);
        ShapePropertyConverter.convertPartialDataFromSdkToUi(afterInheritSdkData, uiData);
        // 通过sdk原始数据与继承后的对比，将自有的属性打标，用来做数据提交时剔除继承属性
        if (rawParagraphList && uiData.paragraphList) {
            TextConverter.addParagraphListRawSignProps(rawParagraphList, uiData.paragraphList);
        }
    }

    public static appendSdkDataInheritProp(beforeInheritSdkData: IShapeData, slideData: ISlideModel): IShapeProps {
        if (!beforeInheritSdkData?.txBody) {
            return beforeInheritSdkData;
        }
        // 通过生成 sdkProps，让sdkData拥有对应slide的默认继承样式
        return new ShapeProps(
            {
                ... { type: DataType.Shape, nvPr: {}, spPr: {}, id: '' },
                ...beforeInheritSdkData,
            },
            (slideData.sdkData as any).getPageContext(),
        );
    }

    /**
     * 获取对应 level 继承的段落样式
     * @param uiData
     * @param level
     * @param keys，指定段落属性的 sdk key 数组，其中项目符号对应多个属性，所以特殊处理使用 'character' 字符串代替
     * @returns
     */
    public static getInheritParagraphProps(uiData: IShapeModel, level: number, keys: Array<string> = []) {
        const levelProps = this.getInheritProps(uiData, level);
        if (!levelProps) {
            return null;
        }
        const partialSdkData: DeepPartial<ICTTextParagraphProperties> = keys.reduce((prev, current) => {
            prev[current] = levelProps[current];
            return prev;
        }, {});
        const partialUiData: IParagraphStyle = {};
        TextParagraphPropsConverter.convertPartialDataFromSdkToUi(partialSdkData, partialUiData);
        if (keys.includes('character')) {
            // 项目符号单独处理
            const character: IParagraphCharacter = {};
            TextParagraphCharacterConverter.convertPartialDataFromSdkToUi(levelProps, character);
            if (Object.keys(character).length) {
                return {
                    ...partialUiData,
                    character,
                };
            }
        }
        return partialUiData;
    }

    private static getInheritProps(uiData: IShapeModel, level: number) {
        // 单元格中继承样式的路径和普通形状不一样
        const textListStyleProps = uiData.inTable
            ? uiData.cellInfo?.txBody?.lstStyle
            : uiData.sdkData?.props?.txBody?.lstStyle;
        if (!textListStyleProps) {
            return null;
        }
        const levelProps = textListStyleProps[`lvl${level + 1}pPr`];
        if (!levelProps) {
            return null;
        }
        return levelProps;
    }
}
